import React, { useState, useEffect } from 'react';
import { FiImage, FiUpload, FiX, FiTrash2 } from 'react-icons/fi';
import axios from '../config/axios';

const NFTIconGalleryModal = ({ isOpen, onClose, onSelectIcon }) => {
  const [icons, setIcons] = useState([]);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadIcons = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/nft-icons');

      // Get base URL for icon paths - use current domain
      const baseUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3001'
        : `${window.location.protocol}//${window.location.hostname}`;

      const formattedIcons = response.data.icons.map(icon => ({
        ...icon,
        fullUrl: baseUrl + icon.url
      }));

      setIcons(formattedIcons);
      setLoading(false);
    } catch (err) {
      console.error('Error loading NFT icons:', err);
      setError('Failed to load NFT icons');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadIcons();
    }
  }, [isOpen]);

  // Handle ESC key to close modal without bubbling up
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isOpen, onClose]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPEG, PNG, GIF, SVG, and WebP images are allowed');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('icon', file);

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      await axios.post('/nft-icons/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      setSuccess(`Icon "${file.name}" uploaded successfully!`);
      setTimeout(() => setSuccess(null), 5000);

      // Reload icons list
      await loadIcons();

      // Reset file input
      event.target.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await axios.delete(`/nft-icons/${filename}`);
      setSuccess('Icon deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      await loadIcons();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.error || 'Failed to delete icon');
    }
  };

  const handleIconSelect = (icon) => {
    setSelectedIcon(icon);
  };

  const handleIconConfirm = () => {
    if (selectedIcon) {
      onSelectIcon(selectedIcon.fullUrl);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-secondary-500 font-heading">NFT Icon Gallery</h2>
            <p className="text-gray-600 text-sm mt-1">Upload and manage NFT icons/emojis</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <FiImage className="w-4 h-4" />
              <span>{icons.length} icons</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Upload Section */}
          <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Icon</h3>
              <span className="text-sm text-gray-500">Max size: 5MB</span>
            </div>

            <input
              type="file"
              id="nftIconUpload"
              className="hidden"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label
              htmlFor="nftIconUpload"
              className="cursor-pointer flex flex-col items-center py-8"
            >
              <FiUpload className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-700 font-medium">
                {uploading ? 'Uploading...' : 'Click to upload NFT icon/emoji'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                JPEG, PNG, GIF, SVG, WebP
              </p>
            </label>

            {uploading && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-secondary-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">{uploadProgress}% uploaded</p>
              </div>
            )}
          </div>

          {/* Success/Error Messages */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">
                <FiX className="w-5 h-5" />
              </button>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                <FiX className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Gallery Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading icons...</div>
            </div>
          ) : icons.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FiImage className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No NFT icons uploaded yet. Upload your first icon above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              {icons.map((icon, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer relative ${
                    selectedIcon?.filename === icon.filename ? 'ring-4 ring-secondary-500' : ''
                  }`}
                  onClick={() => handleIconSelect(icon)}
                >
                  <div className="aspect-square bg-gray-100 relative overflow-hidden p-4 flex items-center justify-center">
                    <img
                      src={icon.fullUrl}
                      alt={icon.filename}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EIcon%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  <div className="p-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500 truncate flex-1" title={icon.filename}>
                      {formatFileSize(icon.size)}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(icon.filename);
                      }}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete icon"
                    >
                      <FiTrash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedIcon ? (
              <span className="font-medium">Selected: {selectedIcon.filename}</span>
            ) : (
              <span>Click an icon to select it</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleIconConfirm}
              disabled={!selectedIcon}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Use Selected Icon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NFTIconGalleryModal;
